import React, { useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Divider,
  Radio,
  Typography,
} from "@mui/material";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { useNavigate } from "react-router-dom";
import { routes } from "@/consts";
import useOrder from "@/hooks/use-order";
import Swal from "sweetalert2";
import { FileUploader } from "react-drag-drop-files";
import { UploadFile } from "@mui/icons-material";

interface Product {
  itemId: string;
  deliveryMethod: string;
  item: string;
  quantity: number;
  price: number;
  discount: number;
  notice: string;
  image: string;
  isCanceled: boolean;
  attachment: string;
  companyId: string;
  company: {
    nameAr: string;
    CompanyNr: string;
  };
}
interface Company {
  id: string;
  nameAr: string;
  nameEn: string;
  address: string;
  region: string;
  city: string;
  CompanyNr: string;
}

interface Invoice {
  fileName: string;
  companyId: string;
  createdDate: string;
  userId: string;
  enVersion: string;
  arVersion: string;
}

interface Tender {
  id: string;
  title: string;
  value: number;
  endDate: string;
  deliverDate: string;
  type: string;
}

interface Order {
  id: string;
  title: string;
  deliverDate: string;
  city: string;
  address: string;
  products: Product[];
  receivedProducts: Product[];
  Sendedproducts: Product[];
  OrderNr: number;
  company: Company;
  buyCompany: Company;
  Tender: Tender;
  status: string;
  DeliveryMethod: string;
  paymentMethod: string;
  invoices: Invoice[];
}

function PaymentMethods({
  order,
  setOrder,
  isTransformationPopupOpen,
  setIsTransformationPopupOpen,
  upsertTransform,
  AvailableCount,
  calculateTotalPrice,
}: {
  order: Order;
  setIsTransformationPopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
  calculateTotalPrice: (products: Product[]) => number;
  isTransformationPopupOpen: boolean;
  upsertTransform: (transformData: any) => Promise<any>;
  setOrder: React.Dispatch<React.SetStateAction<Order | null>>;
  AvailableCount: number;
}) {
  const { locale } = useLocale();
  const { updateOrder: updateOrderApi } = useOrder();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isSendingOrder, setIsSendingOrder] = useState(false);
  const handleFileUpload = (file: File) => {
    setUploadedFile(file);
  };
  const navigate = useNavigate();
  const handleSendOrderWithTransformation = async () => {
    if (!order || !uploadedFile) return;

    try {
      setIsSendingOrder(true);
      const transformationPayload = {
        orderId: order?.id,
        totalPrice:
          parseInt(order.paymentMethod) === 0
            ? -calculateTotalPrice(order.products)
            : calculateTotalPrice(order.products),
        attachment: uploadedFile,
        title: `Transformation for Order ${order?.id}`,
        status: parseInt(order.paymentMethod) === 0 ? "Accept" : "Pending",
        buyerId: order.buyCompany?.id,
        sellerId: order.company?.id,
        products: JSON.stringify(order.products),
        transformRequest: false,
        type: "OrderTransfrom",
      };

      const transformResponse = await upsertTransform(transformationPayload);

      if (transformResponse?.isSuccess) {
        const { Tender, company, buyCompany, ...updateOrder } = order;
        const orderPayload = {
          ...updateOrder,
          status: "Accepted",
        };

        const orderResponse = await updateOrderApi(orderPayload);

        if (orderResponse?.isSuccess) {
          navigate(routes.orders);
          Swal.fire({
            icon: "success",
            title: t("alerts.success.orderReceived"),
            html: `
              <p>${t("alerts.info.orderNumber")} <strong>#${
              order.OrderNr
            }</strong></p>
              <p>${t(
                "alerts.info.invoiceDetails"
              )} <a target="_blank" style="color: #1976d2; text-decoration: underline;">${t(
              "alerts.info.purchaseInvoice"
            )}</a></p>
            `,
            showConfirmButton: true,
            confirmButtonText: t("actions.accept"),
          });
          setIsTransformationPopupOpen(false);
        } else {
          alert(t("alerts.error.sendOrderFailed"));
        }
      } else {
        alert(t("alerts.error.transformationFailed"));
      }
    } catch (error) {
      console.error("Error sending order with transformation:", error);
      alert(t("alerts.error.ErrorhandlingOrder"));
    } finally {
      setIsSendingOrder(false);
    }
  };

  const { t } = useTranslate(locale);
  const paymentMethods = [
    {
      value: 2,
      icon: (
        <img
          src="/icons/visa.png"
          alt="Visa"
          style={{ width: 60, height: "auto" }}
        />
      ),
      label: "Visa",
      disabled: true,
    },
    {
      value: 3,
      icon: (
        <img
          src="/icons/mastercard.png"
          alt="MasterCard"
          style={{ width: 60, height: "auto" }}
        />
      ),
      label: "MasterCard",
      disabled: true,
    },
    {
      value: 1,
      icon: <AccountBalanceIcon sx={{ color: "#1976d2", fontSize: 30 }} />,
      label: "تحويل بنكي",
      disabled: false,
    },
    {
      value: 0,
      icon: (
        <AccountBalanceWalletIcon sx={{ color: "#1976d2", fontSize: 30 }} />
      ),
      label: t("navigation.Wallet"),
      disabled: true,
    },
  ];

  const bankInfo = {
    accountName: "شركة ميدا المتقدمة لحلول الاعمال",
    accountNumber: "212000010006086127973",
    iban: "SA0780000212608016127973",
    bankName: "بنك الراجحي",
  };

  const handlePaymentMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const paymentMethod = event.target.value;
    setOrder((prevOrder: any) => ({
      ...prevOrder,
      paymentMethod: paymentMethod,
    }));
  };
  return (
    <Card
      sx={{
        mb: 4,
        borderRadius: 3,
        boxShadow: "none",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 3,
            color: "#1a1a1a",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          💳 {t("payment.details")}
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
          {t("payment.method")}:
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: 2,
            mb: 3,
          }}
        >
          {paymentMethods.map((method) => (
            <ButtonBase
              key={method?.value}
              onClick={() =>
                handlePaymentMethodChange({
                  target: { value: method.value.toString() },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              disabled={method.disabled}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                border:
                  order.paymentMethod === method.value.toString()
                    ? "2px solid #1976d2"
                    : "1px solid #e0e0e0",
                borderRadius: 3,
                padding: 3,
                background: method.disabled ? "#f5f5f5" : "#fff",
                transition: "all 0.3s ease",
                "&:hover": {
                  border: "2px solid #1976d2",
                  backgroundColor: "#f8f9fa",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  opacity: 0.5,
                  cursor: "not-allowed",
                },
              }}
            >
              {method.icon}
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  fontWeight: 600,
                  color: method.disabled ? "#999" : "#333",
                }}
              >
                {method.label}
              </Typography>
              <Radio value={method.value} sx={{ display: "none" }} />
            </ButtonBase>
          ))}
        </Box>

        {/* Payment Details */}
        <Box sx={{ mt: 3 }}>
          {parseInt(order.paymentMethod) === 1 && (
            <Card
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                {t("payment.bankTransfer")}:
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("payment.bankName")}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {bankInfo.bankName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("item.fields.name")}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {bankInfo.accountName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("payment.accountNumber")}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {bankInfo.accountNumber}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("payment.iban")}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {bankInfo.iban}
                  </Typography>
                </Box>
              </Box>
            </Card>
          )}

          {parseInt(order.paymentMethod) === 0 && (
            <Card
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                {t("wallet.details")}:
              </Typography>
              <Typography variant="body2">
                <strong>{t("wallet.currentBalance")}:</strong>{" "}
                {AvailableCount.toFixed(2)} {t("currency")}
              </Typography>
            </Card>
          )}
        </Box>
      </CardContent>
      <Dialog
        open={isTransformationPopupOpen}
        onClose={() => setIsTransformationPopupOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1.25rem",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          {t("payment.confirmTransfer")}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
              <strong>{t("orders.fields.totalPrice")}:</strong>{" "}
              <span style={{ color: "#1976d2", fontSize: "1.1rem" }}>
                {calculateTotalPrice(order?.products || []).toFixed(2)}{" "}
                {t("currency")}
              </span>
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
              {t("payment.bankInfo")}:
            </Typography>
            <Card
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("payment.accountName")}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {bankInfo.accountName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("payment.accountNumber")}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {bankInfo.accountNumber}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("payment.iban")}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {bankInfo.iban}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("payment.bankName")}:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {bankInfo.bankName}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
            {t("payment.uploadTransferImage")}:
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FileUploader
              handleChange={handleFileUpload}
              name="file"
              types={["jpg", "png", "pdf"]}
              maxSize={5}
              label={t("general.DragOrClickToUpload")}
            />
            {uploadedFile && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <UploadFile color="primary" />
                <Typography variant="body2" fontWeight={600}>
                  {uploadedFile.name}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setIsTransformationPopupOpen(false)}
            sx={{
              borderColor: "#e0e0e0",
              color: "#666",
              "&:hover": {
                borderColor: "#ccc",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            {t("actions.cancel")}
          </Button>
          <Button
            onClick={handleSendOrderWithTransformation}
            disabled={!uploadedFile || isSendingOrder}
            sx={{
              backgroundColor: "#18181b",
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              "&:disabled": {
                backgroundColor: "#e0e0e0",
                color: "#999",
              },
            }}
          >
            {isSendingOrder ? t("actions.submitting") : t("actions.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default PaymentMethods;
